import { useState, useEffect, useRef } from 'react'
import { FileText, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useSearch } from '@tanstack/react-router'
import { Layout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { useWritingAssistants } from '@/lib/hooks/writingAssistant/useWritingAssistants'
import { useWritingAssistantContent } from '@/lib/hooks/writingAssistant/useWritingAssistantContent'
import { useWritingAssistantTranslation } from '@/lib/hooks/writingAssistant/useWritingAssistantTranslation'
import { useWritingAssistantUIStates } from '@/lib/hooks/writingAssistant/useWritingAssistantUIStates'
import { useCrisisStore } from '@/lib/stores/crisisStore'
import { useToast } from '@/lib/hooks/useToast'
import { WritingAssistantCreateForm } from './components/WritingAssistantCreateForm'
import { WritingAssistantCard } from './components/WritingAssistantCard'
import { WritingAssistantFilterBar } from './components/WritingAssistantFilterBar'
import { writingAssistantService } from '@/lib/services/writingAssistantService'
import { useHasPermission } from '@/lib/hooks/usePermission'
export function WritingAssistantListPage() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const searchParams = useSearch({ strict: false }) as { selectedId?: string } | undefined
  const [searchQuery, setSearchQuery] = useState('')
  const [contentTypeFilter, setContentTypeFilter] = useState<string>('all')
  const [createdByFilter, setCreatedByFilter] = useState<string>('all')
  const selectedItemRef = useRef<HTMLDivElement>(null)
  const expandedIdRef = useRef<string | undefined>(undefined)
  const previousWritingAssistantsRef = useRef<typeof writingAssistants>([])

  const { activeCrisis } = useCrisisStore()
  const isClosedCrisis = activeCrisis?.status === 'closed'

  const canCreateWriting = useHasPermission('writing.create')

  // Fetch writing assistants
  const { writingAssistants, isLoading, error, refetch, updateUnreadyWritingAssistants } =
    useWritingAssistants(activeCrisis?.id)

  // UI states management
  const {
    minimizedStates,
    toggleMinimized,
    manualSaveLoading,
    setManualSaveLoading,
    saveSuccess,
    showSaveSuccess,
    additionalInstructions,
    setAdditionalInstructions,
  } = useWritingAssistantUIStates(writingAssistants)

  // Track the currently "active" writing document for real-time collaboration.
  // When a card is expanded, it becomes the active document and the WebSocket
  // document room is joined so content changes are broadcast to collaborators.
  const [activeWritingId, setActiveWritingId] = useState<string | null>(null)
  const activeWritingInitializedRef = useRef(false)

  // Once minimizedStates are populated (by useWritingAssistantUIStates), set the
  // initially expanded card as the active document exactly once.
  useEffect(() => {
    if (activeWritingInitializedRef.current) return
    const expanded = writingAssistants.find(wa => minimizedStates[wa.id] === false)
    if (expanded) {
      activeWritingInitializedRef.current = true
      setActiveWritingId(expanded.id)
    }
  }, [writingAssistants, minimizedStates])

  // When the user expands a card, make it the active document.
  const handleToggleMinimized = (waId: string) => {
    const willExpand = minimizedStates[waId] !== false
    toggleMinimized(waId)
    if (willExpand) {
      setActiveWritingId(waId)
    }
  }

  // Translation management
  const { selectedLanguages, translatingStates, handleLanguageChange } =
    useWritingAssistantTranslation({
      writingAssistants,
      updateUnreadyWritingAssistants,
      isClosedCrisis,
    })

  // Content management
  const {
    getContentState,
    isSaving,
    handleContentChange,
    saveContent,
    getSavingStatusText,
    getCurrentContent,
    getCollaborators,
  } = useWritingAssistantContent({
    writingAssistants,
    selectedLanguages,
    isClosedCrisis,
    activeWritingId,
  })

  // Poll for active jobs
  useEffect(() => {
    const hasActiveWA = writingAssistants.some(
      wa => wa.status === 'queued' || wa.status === 'processing'
    )

    if (hasActiveWA) {
      const intervalId = setInterval(() => {
        updateUnreadyWritingAssistants()
      }, 2000)

      return () => clearInterval(intervalId)
    }
    return undefined
  }, [writingAssistants, updateUnreadyWritingAssistants])

  // Check for status changes and show toast for failures
  useEffect(() => {
    if (previousWritingAssistantsRef.current.length > 0) {
      writingAssistants.forEach(currentWA => {
        const previousWA = previousWritingAssistantsRef.current.find(wa => wa.id === currentWA.id)
        if (previousWA && previousWA.status !== 'failed' && currentWA.status === 'failed') {
          toast({
            title: 'Fout bij genereren van tekst',
            description:
              'Er is een fout opgetreden bij het verbinden met de AI-service. Probeer het opnieuw.',
            variant: 'destructive',
          })
        }
      })
    }
    previousWritingAssistantsRef.current = writingAssistants
  }, [writingAssistants, toast])

  // Auto-expand and scroll to selected writing assistant (only once per selection)
  useEffect(() => {
    if (searchParams?.selectedId && writingAssistants.length > 0) {
      // Check if the selected item exists in the current list
      const selectedItemExists = writingAssistants.some(wa => wa.id === searchParams.selectedId)

      if (selectedItemExists && minimizedStates[searchParams.selectedId] !== undefined) {
        // Only expand if we haven't already processed this ID
        if (expandedIdRef.current !== searchParams.selectedId) {
          expandedIdRef.current = searchParams.selectedId

          // If it's minimized, toggle it to expand
          if (minimizedStates[searchParams.selectedId] === true) {
            toggleMinimized(searchParams.selectedId)
          }

          // Scroll to the selected item
          setTimeout(() => {
            selectedItemRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }, 100)
        }
      }
    }
  }, [searchParams?.selectedId, writingAssistants.length, minimizedStates, toggleMinimized, writingAssistants])

  // Filter and sort writing assistants
  const filteredWritingAssistants = writingAssistants
    .filter(wa => {
      // Search filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase()
        const matchesSearch =
          wa.contentType.toLowerCase().includes(searchLower) ||
          (wa.generatedContent && wa.generatedContent.toLowerCase().includes(searchLower))
        if (!matchesSearch) return false
      }

      // Content type filter
      if (contentTypeFilter !== 'all' && wa.contentType !== contentTypeFilter) {
        return false
      }

      // Created by filter
      if (createdByFilter !== 'all' && wa.createdBy?.id !== createdByFilter) {
        return false
      }

      return true
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())

  // Action handlers
  const handleManualSave = async (writingAssistantId: string) => {
    if (isClosedCrisis) return

    const htmlContent = getContentState(writingAssistantId)
    if (!htmlContent) return

    const selectedLanguage = selectedLanguages[writingAssistantId] || 'nl'

    setManualSaveLoading(prev => ({ ...prev, [writingAssistantId]: true }))

    try {
      await saveContent(writingAssistantId, htmlContent, selectedLanguage)
      showSaveSuccess(writingAssistantId)
    } catch (error) {
      console.error('Error saving content:', error)
    } finally {
      setManualSaveLoading(prev => ({ ...prev, [writingAssistantId]: false }))
    }
  }

  const handleAdjustContent = async (writingAssistantId: string) => {
    if (isClosedCrisis) return

    const instructions = additionalInstructions[writingAssistantId]
    if (!instructions || !instructions.trim()) return

    try {
      await writingAssistantService.adjustWritingAssistantContent(writingAssistantId, instructions)
      setAdditionalInstructions(prev => ({ ...prev, [writingAssistantId]: '' }))
      refetch()
    } catch (err) {
      console.error('Error adjusting content:', err)
    }
  }

  const handleDelete = async (writingAssistantId: string) => {
    if (isClosedCrisis) return

    if (!confirm('Weet je zeker dat je deze tekst wilt verwijderen?')) return

    try {
      await writingAssistantService.deleteWritingAssistant(writingAssistantId)
      refetch()
    } catch (err) {
      console.error('Error deleting:', err)
    }
  }

  const handleCopy = (writingAssistantId: string) => {
    const htmlContent = getContentState(writingAssistantId)
    if (htmlContent) {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = htmlContent
      const plainText = tempDiv.textContent || tempDiv.innerText || ''
      navigator.clipboard.writeText(plainText)
    }
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 md:px-6 mt-4">
        <h1 className="text-2xl md:text-4xl font-bold text-foreground mb-2">
          {t('writing.titel')}
        </h1>
        <p className="text-base md:text-xl text-muted-foreground">{t('writing.beschrijving')}</p>
      </div>
      <div className="container mx-auto md:px-6 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-0 md:gap-8">
          {/* Left Column - Create Form */}
          {canCreateWriting && (
            <div className="lg:col-span-1 px-4 py-6 md:px-0 md:py-0">
              <WritingAssistantCreateForm onSuccess={refetch} isClosedCrisis={isClosedCrisis} />
            </div>
          )}

          {/* Right Column - Messages List */}
          <div className={`${canCreateWriting ? 'lg:col-span-3' : 'lg:col-span-4'} px-4 py-2 md:px-0 md:py-0`}>
            <WritingAssistantFilterBar
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              contentTypeFilter={contentTypeFilter}
              onContentTypeFilterChange={setContentTypeFilter}
              createdByFilter={createdByFilter}
              onCreatedByFilterChange={setCreatedByFilter}
              writingAssistants={writingAssistants}
            />

            {isLoading ? (
              <Card className="text-center py-8 md:py-12 rounded-lg border-x mx-0 md:mx-0">
                <CardContent>
                  <Loader2 className="w-16 h-16 text-muted-foreground mx-auto mb-4 animate-spin" />
                  <h3 className="text-xl font-semibold mb-2">{t('writing.berichtenLaden')}</h3>
                  <p className="text-muted-foreground">{t('writing.berichtenLadenBeschrijving')}</p>
                </CardContent>
              </Card>
            ) : error ? (
              <Card className="text-center py-8 md:py-12 rounded-lg border-x mx-0">
                <CardContent>
                  <FileText className="w-12 h-12 md:w-16 md:h-16 text-red-400 mx-auto mb-4" />
                  <h3 className="text-lg md:text-xl font-semibold mb-2">
                    {t('writing.foutBijLaden')}
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground mb-6">
                    {t('writing.foutBijLadenBeschrijving', { error })}
                  </p>
                  <Button onClick={() => refetch()} variant="outline">
                    {t('writing.opnieuwProberen')}
                  </Button>
                </CardContent>
              </Card>
            ) : filteredWritingAssistants.length === 0 ? (
              <Card className="text-center py-8 md:py-12 rounded-none md:rounded-lg border-x-0 md:border-x mx-0 md:mx-0">
                <CardContent>
                  <FileText className="w-12 h-12 md:w-16 md:h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg md:text-xl font-semibold mb-2">
                    {t('writing.geenBerichtenGevonden')}
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    {searchQuery
                      ? t('writing.geenBerichtenGevondenZoeken')
                      : t('writing.geenBerichtenNogNiet')}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="flex flex-col gap-0 gap-3 md:gap-6">
                {filteredWritingAssistants.map(wa => (
                  <div
                    key={wa.id}
                    ref={searchParams?.selectedId === wa.id ? selectedItemRef : null}
                  >
                    <WritingAssistantCard
                      writingAssistant={wa}
                      isClosedCrisis={isClosedCrisis}
                      isMinimized={minimizedStates[wa.id] || false}
                      selectedLanguage={selectedLanguages[wa.id] || 'nl'}
                      isTranslating={!!translatingStates[wa.id]}
                      isSaving={isSaving[wa.id] || false}
                      manualSaveLoading={manualSaveLoading[wa.id] || false}
                      saveSuccess={saveSuccess[wa.id] || false}
                      additionalInstructions={additionalInstructions[wa.id] || ''}
                      contentState={getContentState(wa.id) || getCurrentContent(wa)}
                      savingStatusText={getSavingStatusText(wa.id)}
                      collaborators={getCollaborators(wa.id)}
                      onToggleMinimized={() => handleToggleMinimized(wa.id)}
                      onLanguageChange={lang => handleLanguageChange(wa.id, lang)}
                      onContentChange={content => handleContentChange(wa.id, content)}
                      onManualSave={() => handleManualSave(wa.id)}
                      onAdditionalInstructionsChange={instructions =>
                        setAdditionalInstructions(prev => ({ ...prev, [wa.id]: instructions }))
                      }
                      onAdjustContent={() => handleAdjustContent(wa.id)}
                      onCopy={() => handleCopy(wa.id)}
                      onDelete={() => handleDelete(wa.id)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  )
}
